import { Link, useParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PUBLIC_ROUTES } from "@/routes/public";
import { getPublishedArticle, type ArticleBlock } from "@/content/articles";
import { PublicContentLayout } from "@/components/public/PublicContentLayout";
import { formatContentDate } from "@/content/articles/dates";

function Block({ block }: { block: ArticleBlock }) {
  switch (block.t) {
    case "h2":
      return <h2 className="mt-8 text-2xl font-semibold text-foreground">{block.text}</h2>;
    case "p":
      return <p className="mt-4 leading-7 text-foreground/90">{block.text}</p>;
    case "ul":
      return (
        <ul className="mt-4 list-disc pl-6 space-y-2 leading-7 text-foreground/90">
          {block.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
  }
}

/** JUB-123: one product guide. Unknown and unpublished slugs show a not-found state. */
export default function BlogArticlePage() {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const { slug = "" } = useParams<{ slug: string }>();
  const article = getPublishedArticle(slug);

  usePageMeta(
    article
      ? {
          title: `${article.title[locale]} | Jubiel`,
          description: article.summary[locale],
          locale,
          path: PUBLIC_ROUTES.blogArticle(locale, article.slug),
          alternates: {
            ro: PUBLIC_ROUTES.blogArticle("ro", article.slug),
            en: PUBLIC_ROUTES.blogArticle("en", article.slug),
          },
          type: "article",
        }
      : {
          title: t("blog.notFound.metaTitle"),
          description: t("blog.notFound.description"),
          locale,
          path: PUBLIC_ROUTES.blogArticle(locale, slug),
          noindex: true,
        },
  );

  const backLink = (
    <Link to={PUBLIC_ROUTES.blog(locale)} className="text-sm font-medium text-pink-600 hover:text-pink-700">
      {t("blog.backToList")}
    </Link>
  );

  if (!article) {
    return (
      <PublicContentLayout>
        <div data-testid="blog-article-not-found">
          <h1 className="text-3xl font-bold text-foreground">{t("blog.notFound.title")}</h1>
          <p className="mt-3 text-muted-foreground">{t("blog.notFound.description")}</p>
          <p className="mt-6">{backLink}</p>
        </div>
      </PublicContentLayout>
    );
  }

  return (
    <PublicContentLayout>
      <p>{backLink}</p>
      <article className="mt-6" data-testid="blog-article">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">{article.title[locale]}</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          <time dateTime={article.updatedAt}>
            {t("blog.updatedOn", { date: formatContentDate(article.updatedAt, locale) })}
          </time>
        </p>
        <p className="mt-4 text-lg text-muted-foreground">{article.summary[locale]}</p>
        {article.body[locale].map((block, index) => (
          <Block key={index} block={block} />
        ))}
        <nav
          aria-label={t("blog.relatedLinksLabel")}
          className="mt-10 border-t pt-6 flex flex-wrap items-center gap-4 text-sm font-medium text-pink-600"
        >
          <Link to={PUBLIC_ROUTES.blog(locale)} className="hover:text-pink-700">
            {t("blog.backToList")}
          </Link>
          <span aria-hidden="true" className="text-muted-foreground">
            •
          </span>
          <Link to={PUBLIC_ROUTES.home(locale)} className="hover:text-pink-700">
            {t("blog.homeLink")}
          </Link>
          <span aria-hidden="true" className="text-muted-foreground">
            •
          </span>
          <Link to={PUBLIC_ROUTES.faq(locale)} className="hover:text-pink-700">
            {t("blog.faqLink")}
          </Link>
        </nav>
      </article>
    </PublicContentLayout>
  );
}
